import { useEffect, useState } from "react";
import { fetchApi } from "../services/services";
import { getErrorMessage } from "../services/utils/apiError";

const dataCache = new Map<string, unknown>();

export interface IApiTableReturn<T> {
  loading: boolean;
  data: T | undefined;
  error: string;
  refetch: () => void;
}
export function useFetch<T>(location: string, options?: { cache?: boolean }): IApiTableReturn<T> {
  const useCache = options?.cache ?? false;
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>("");
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = () => {
    setRefreshKey((key) => key + 1);
  };

  useEffect(() => {
    let cancelled = false;
    const cached = useCache ? (dataCache.get(location) as T | undefined) : undefined;

    if (cached !== undefined) {
      setData(cached);
      setLoading(false);
    } else {
      setData(undefined);
      setLoading(true);
    }
    setError("");

    const fetchData = async () => {
      try {
        const list = await fetchApi<T>(location);
        if (cancelled) return;
        if (useCache) {
          dataCache.set(location, list);
        }
        setData(list);
      } catch (error) {
        if (!cancelled) {
          setError(getErrorMessage(error));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void fetchData();

    return () => {
      cancelled = true;
    };
  }, [location, refreshKey, useCache]);

  return { loading, data, error, refetch };
}
